import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 22, 2026">
      <div className="legal-callout">
        We believe in plain language. This policy explains exactly what data we collect, why we
        collect it, and what you can do about it.
      </div>

      <h2>1. Who we are</h2>
      <p>ordrsAI ("we," "us," or "our") is a software company operating at ordrs.ai. Our platform provides AI-powered order-intake software to businesses ("users"). This Privacy Policy applies to our website, application, and all related services (collectively, the "Service").</p>
      <p>By using the Service, you agree to this policy. Questions? Email us at <a href="mailto:support@ordrs.ai">support@ordrs.ai</a>.</p>

      <h2>2. Information we collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account data:</strong> Name, email address, and password when you sign up.</li>
        <li><strong>Profile data:</strong> Additional information you add (e.g., company name, role).</li>
        <li><strong>Payment data:</strong> Billing address and payment method. Card details are processed by our PCI-DSS-compliant payment processor — we never store full card numbers.</li>
        <li><strong>Content you create:</strong> Any inputs, menus, prompts, or data you submit to the Service.</li>
        <li><strong>Support communications:</strong> Messages you send us via email or in-app support.</li>
      </ul>
      <h3>Information we collect automatically</h3>
      <ul>
        <li><strong>Usage data:</strong> Features used, pages visited, actions taken, session duration, and error logs.</li>
        <li><strong>Device and browser data:</strong> IP address, browser type, operating system, and device identifiers.</li>
        <li><strong>Location data:</strong> Approximate location from your IP, or precise GPS where a feature requests it with permission.</li>
        <li><strong>Cookies and tracking:</strong> See our <Link to="/cookies">Cookie Policy</Link>.</li>
      </ul>
      <h3>Information from third parties</h3>
      <ul>
        <li><strong>OAuth providers:</strong> Name, email, and profile picture if you sign in via Google or another provider.</li>
        <li><strong>Payment processors:</strong> Transaction status and fraud signals.</li>
        <li><strong>Analytics providers:</strong> Aggregated usage data.</li>
      </ul>

      <h2>3. How we use your information</h2>
      <table>
        <thead><tr><th>Purpose</th><th>Legal basis (US)</th></tr></thead>
        <tbody>
          <tr><td>Provide, operate, and improve the Service</td><td>Contract / legitimate interest</td></tr>
          <tr><td>Process payments and prevent fraud</td><td>Contract / legal obligation</td></tr>
          <tr><td>Send transactional emails (receipts, alerts)</td><td>Contract</td></tr>
          <tr><td>Send product updates and marketing emails</td><td>Consent (opt-out available)</td></tr>
          <tr><td>Provide customer support</td><td>Legitimate interest</td></tr>
          <tr><td>Analyze usage and improve features</td><td>Legitimate interest</td></tr>
          <tr><td>Comply with legal obligations</td><td>Legal obligation</td></tr>
        </tbody>
      </table>

      <h2>4. How we share your information</h2>
      <p>We do not sell your personal information. We share data only in these circumstances:</p>
      <ul>
        <li><strong>Service providers:</strong> Vendors who help us operate the Service (hosting, analytics, payments, support). They may only process data as we instruct.</li>
        <li><strong>Legal requirements:</strong> Where required by law, subpoena, or court order.</li>
        <li><strong>Business transfers:</strong> In a merger, acquisition, or sale of assets, data may transfer to the new entity. We will notify you first.</li>
        <li><strong>With your consent:</strong> Any other sharing we describe at the time of collection.</li>
      </ul>

      <h2>5. Third-party integrations</h2>
      <p>You may connect the Service to third-party tools (e.g., Stripe, Twilio, CRM platforms). When you authorize such integrations, you authorize data to flow between ordrsAI and those services. We are not responsible for the privacy practices of third-party services you connect.</p>

      <h2>6. Location data</h2>
      <p>We collect approximate location from your IP address by default. If a feature requests precise location, we will ask for explicit permission. You may deny or revoke location access via your device or browser settings at any time.</p>

      <h2>7. Data security</h2>
      <p>We use industry-standard security measures including TLS encryption in transit, encryption at rest, access controls, and regular security reviews. No system is 100% secure — if you believe your account has been compromised, contact us immediately at <a href="mailto:support@ordrs.ai">support@ordrs.ai</a>.</p>

      <h2>8. Data retention</h2>
      <p>We retain your data for as long as your account is active or as needed to provide the Service. You may delete your account at any time. We may retain certain data after deletion as required by law or for legitimate business purposes such as fraud prevention.</p>

      <h2>9. Children's privacy</h2>
      <p>The Service is not directed to children under 13. We do not knowingly collect data from children. If you believe we have inadvertently collected a child's data, contact us at <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> and we will delete it promptly.</p>

      <h2>10. Your rights</h2>
      <ul>
        <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
        <li><strong>Correction:</strong> Update inaccurate information via account settings or by emailing us.</li>
        <li><strong>Deletion:</strong> Delete your account and associated data via settings or by contacting us.</li>
        <li><strong>Opt-out of marketing:</strong> Unsubscribe via any marketing email or your notification preferences.</li>
        <li><strong>Data portability:</strong> Request an export of your data in a machine-readable format.</li>
      </ul>
      <p><strong>California residents (CCPA/CPRA):</strong> You have the right to know what personal information we collect, request deletion, and opt out of the sale or sharing of your data (we do not sell your data). Submit requests to <a href="mailto:support@ordrs.ai">support@ordrs.ai</a>.</p>
      <p>To exercise any right, email <a href="mailto:support@ordrs.ai">support@ordrs.ai</a>. We will respond within 30 days.</p>

      <h2>11. Changes to this policy</h2>
      <p>We may update this policy from time to time. We will notify you of material changes by email or prominent notice on the Service before they take effect. Continued use after changes constitutes acceptance.</p>

      <h2>12. Contact</h2>
      <p>ordrsAI · <a href="mailto:support@ordrs.ai">support@ordrs.ai</a></p>
    </LegalLayout>
  );
}
